import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, useAuth } from "@/App";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");
  
  // Check if user is B2B (from localStorage)
  const isB2B = localStorage.getItem("cliptour_isB2B") === "true";
  const homePath = isB2B ? "/partners" : "/";
  
  // Get service type to display correct delivery time
  const serviceType = localStorage.getItem("cliptour_last_service");
  const deliveryTime = serviceType === "cliptour" ? "24" : "48";

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/checkout/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error("Failed to check payment status");
      }

      const data = await response.json();

      if (data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts((prev) => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-success-page">
      <Header homePath={homePath} />

      <section className="section-spacing pt-32">
        <div className="container-custom">
          <motion.div 
            className="max-w-lg mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {status === "checking" && (
              <div className="bg-white rounded-3xl border border-slate-200 p-12">
                <Loader2 className="w-16 h-16 text-#3C59B2 mx-auto mb-6 animate-spin" />
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing payment...</h1>
                <p className="text-slate-600">Please wait while we confirm your payment.</p>
              </div>
            )}

            {status === "success" && (
              <div className="bg-white rounded-3xl border border-slate-200 p-12">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment successful!</h1>
                <p className="text-slate-600 mb-8">
                  Thank you for your order. We'll deliver your videos within {deliveryTime} business hours.
                  You'll receive a confirmation email shortly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
                    data-testid="go-to-dashboard-btn"
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="rounded-full px-8"
                  >
                    Back to Home
                  </Button>
                </div>
              </div>
            )}

            {status === "error" && (
              <div className="bg-white rounded-3xl border border-slate-200 p-12">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Payment issue</h1>
                <p className="text-slate-600 mb-8">
                  We couldn't confirm your payment. If you were charged, please contact us at{" "}
                  <a href="mailto:contact@cliptour.ai" className="text-#3C59B2 hover:underline">
                    contact@cliptour.ai
                  </a>
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate("/order")}
                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8"
                  >
                    Try Again
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/contact")}
                    className="rounded-full px-8"
                  >
                    Contact Support
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
